// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
// @greenlight-spec: decisions/0030-connected-database-proxied-sql-delivery.md
// @greenlight-spec: 09-data-brokering.md
/**
 * Both Fabric entries stand on one Entra app registration, so the first three steps
 * are shared: the app itself, the tenant setting that admits service principals to
 * Fabric at all, and the workspace grant.
 */
const SHARED_ENTRA_STEPS = [
    {
        title: 'Create a dedicated Entra app (not Greenlight SSO)',
        body: 'In the Entra admin center, register a new app (e.g. "Greenlight Fabric Reader") and add a client secret under Certificates & secrets.\n' +
            'Note the Application (client) ID, the Directory (tenant) ID, and the secret value — the secret is shown once.\n' +
            'Do not reuse the Greenlight SSO / OIDC app registration: keep login and data-read machine identity separate.',
    },
    {
        title: 'Let service principals use Fabric (tenant setting)',
        body: 'A Fabric admin opens Admin portal → Tenant settings → Developer settings → "Service principals can use Fabric APIs" and enables it.\n' +
            "This setting is normally scoped to specific security groups rather than the whole tenant. If it is, add the app registration's service principal to one of those groups — without that membership every later step fails with an opaque permissions error.",
    },
    {
        title: 'Give the service principal workspace access',
        body: 'In the Fabric workspace, Manage access → Add people or groups → the app registration. Any workspace role (Admin, Member, Contributor, Viewer) is enough to connect; Viewer is least privilege.\n' +
            "Tighter still: skip the workspace role and use the item's Manage permissions → Add user with no additional permissions, then grant read in T-SQL against the warehouse:\n\n" +
            '  GRANT SELECT ON <schema>.<object> TO [<app-display-name>];',
    },
    {
        title: 'Bootstrap the service principal against the Fabric REST API',
        body: "Fabric does not establish the service principal's security token when it connects to the SQL endpoint — only a successful Fabric REST API call does.\n" +
            'Make one call as the app (for example GET https://api.fabric.microsoft.com/v1/workspaces/<workspace-id>/items) before relying on it, and repeat at least every 30 days: the token expires on that cadence and a lapsed one surfaces as a permissions failure, not an auth failure.\n' +
            'Registering the Microsoft Fabric (REST API) entry alongside the warehouse is the well-lit way to keep this honest.',
    },
];
/**
 * The service-principal setup guide for the ticked Fabric pieces: the shared Entra
 * steps once, the SQL endpoint steps when SQL is ticked, and one registration step.
 */
export function microsoftFabricServicePrincipalSteps(pieces) {
    const sql = pieces.includes('sql');
    const rest = pieces.includes('rest');
    const steps = [...SHARED_ENTRA_STEPS];
    if (sql) {
        steps.push({
            title: 'Copy each SQL connection string',
            body: 'In the Fabric portal, open the warehouse, lakehouse SQL analytics endpoint, or SQL database → Settings → SQL connection string. A Warehouse or SQL analytics endpoint looks like <guid>.datawarehouse.fabric.microsoft.com and its database is the item name; a Fabric SQL database looks like <guid>.database.fabric.microsoft.com and its database is the <name>-<guid> in its connection string.\n' +
                'Register the host with port 1433, and always set the database — omitting it lands the connection on master, where none of your tables exist.',
        }, {
            title: 'Open the firewall as MSSQL/TDS, not HTTPS',
            body: "On a private install whose outbound traffic leaves through a firewall you run, allow each SQL endpoint host on TCP 1433. Separately, if the workspace has IP firewall rules (Workspace settings → Inbound networking), add Greenlight's outbound IP address to its list. Only Greenlight itself connects, never app pods or laptops.\n" +
                'Configure the rule as MSSQL/TDS traffic. A protocol-aware firewall that inspects 1433 as HTTPS breaks the connection even with the port open, and the failure reads like an unreachable host.',
        });
    }
    steps.push({
        title: 'Register in Greenlight',
        body: [
            ...(sql ? ["Enter each SQL endpoint's host, port 1433, and database name."] : []),
            'Put the Application (client) ID in Client ID on this form, the Directory (tenant) ID in the Directory (tenant) ID field, and paste only the client secret under Credentials. Greenlight vaults the secret and never exposes it to apps.',
            ...(rest
                ? [
                    'For the REST API, Greenlight mints a Fabric bearer via client credentials against https://login.microsoftonline.com/<tenant-id>/oauth2/v2.0/token with scope https://api.fabric.microsoft.com/.default.',
                ]
                : []),
            ...(sql
                ? [
                    'For SQL, Greenlight connects over TDS as that Entra service principal. Fabric does not support SQL authentication, so there is no username/password alternative.',
                ]
                : []),
        ].join('\n'),
    });
    return steps;
}
/** Fabric REST API (HTTPS, client credentials) — the proxied Tier-2 entry. */
export const MICROSOFT_FABRIC_SERVICE_PRINCIPAL_STEPS = microsoftFabricServicePrincipalSteps(['rest']);
/** A Fabric SQL endpoint (TDS 1433) — the connected database. */
export const FABRIC_WAREHOUSE_SERVICE_PRINCIPAL_STEPS = microsoftFabricServicePrincipalSteps(['sql']);
//# sourceMappingURL=microsoft-fabric-service-principal-guide.js.map